import { icons } from '@ublend-npm/aulaui-next';
import React, { useRef, useState } from 'react';
import { Element } from 'slate';
import { BlockMenuItemRenderer, BlockMenuNode } from '../../plugins/plugins';
import {
  NestedMenuContainer,
  NestedMenuItemText,
  WrappedMenuItem,
} from './NestedMenu.styled';

export type NestedMenuProps = {
  element: Element;
  open: boolean;
  disablePortal: boolean;
  onClose: () => void;
  openFileDialog: () => void;
  anchorEl: HTMLElement;
  text?: string;
  path: string[];
  setPath: (path: string[]) => void;
  currentPath: string[];
  menuItems?: BlockMenuNode;
  renderSubMenuItem?: BlockMenuItemRenderer;
  hideBackdrop?: boolean;
  anchorOrigin?: any;
} & React.HTMLAttributes<HTMLDivElement>;

type NestedMenuItemProps = {
  autoFocus?: boolean;
  leftIcon?: JSX.Element;
  text?: string;
  onClose?: () => void;
  rightIcon?: JSX.Element;
  attributes?: any;
  children?: any;
};

export const NestedMenuItem = React.forwardRef(
  (
    {
      leftIcon,
      text,
      rightIcon,
      onClose,
      attributes,
      children,
      autoFocus,
    }: NestedMenuItemProps,
    ref,
  ) => (
    <WrappedMenuItem
      ref={ref}
      key={text}
      aria-label={text}
      style={{ pointerEvents: 'auto' }}
      onClose={onClose}
      autoFocus={autoFocus}
      {...attributes}
    >
      {leftIcon}
      <NestedMenuItemText>{text}</NestedMenuItemText>
      {rightIcon}
      {children}
    </WrappedMenuItem>
  ),
);

NestedMenuItem.displayName = 'NestedMenuItem';

export const DefaultMenuItem: BlockMenuItemRenderer = ({
  text,
  children,
  onClose,
  attributes,
}) =>
  text === 'Assignment' ? (
    <NestedMenuItem
      text={text}
      key={text}
      onClose={onClose}
      leftIcon={<icons.AssignmentIcon />}
      rightIcon={<icons.ArrowRight />}
      attributes={attributes}
    >
      {children}
    </NestedMenuItem>
  ) : (
    <NestedMenuItem
      text={text}
      key={text}
      onClose={onClose}
      rightIcon={<icons.ArrowRight />}
      attributes={attributes}
    >
      {children}
    </NestedMenuItem>
  );

export const RecursiveNestedMenu = ({
  menuItems,
  element,
  text,
  openFileDialog,
  renderSubMenuItem = DefaultMenuItem,
  path,
  setPath,
  currentPath,
  onClose,
  open,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  anchorEl,
  anchorOrigin,
  disablePortal,
}: NestedMenuProps) => {
  let keys =
    menuItems != null && typeof menuItems !== 'function'
      ? Object.keys(menuItems)
      : [];

  // Currently the order of menu items is given by the order in which the plugins are inserted
  if (text === 'Integrations') {
    keys = keys.sort((k1, k2) => (k1 < k2 ? -1 : 1));
  }

  return menuItems != null ? (
    <NestedMenuContainer
      MenuListProps={{ disablePadding: true }}
      data-testid="block-menu-dropdown"
      tabIndex={-1}
      style={{ pointerEvents: 'none', zIndex: 1500 }}
      key={text || 'root'}
      getContentAnchorEl={null}
      open={open}
      onKeyDown={onKeyDown}
      anchorEl={anchorEl}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disablePortal={disablePortal}
      PaperProps={{
        style: {
          pointerEvents: 'all',
          // mentioned in assignment designs
          maxHeight: 420,
        },
      }}
      anchorOrigin={
        anchorOrigin || {
          vertical: 'bottom',
          horizontal: 'right',
        }
      }
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      {typeof menuItems === 'function'
        ? menuItems({ element, text, onClose, openFileDialog })
        : keys.map((key) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const menuItemRef = useRef<HTMLButtonElement>();

            const nestedPath = [...path, key];
            const isOpen =
              nestedPath.length > 0 &&
              nestedPath.every(
                (val: string, index: number) => currentPath[index] === val,
              );

            const _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (menuItemRef.current == null) {
                throw new Error('menu Item ref must be defined');
              }

              if (
                /**
                 * We need to stop event propagation in two instances here:
                 *
                 * 1. To prevent event handlers in web-app from submitting a message
                 */
                (menuItemRef.current !== document.activeElement &&
                  !['Enter', ' '].includes(event.key)) ||
                /**
                 * 2. To prevent event handlers in web-app from closing a modal
                 */
                (['Esc', 'Escape'].includes(event.key) && text == null)
              ) {
                event.stopPropagation();
              }
            };

            const handleKeyUp = (event: React.KeyboardEvent<HTMLElement>) => {
              switch (event.key) {
                case 'Esc':
                case 'Escape':
                  if (currentPath.length === 0) {
                    onClose();
                  } else {
                    setPath(path);
                  }
                  break;
                case 'Left':
                case 'ArrowLeft':
                  setPath(path);
                  break;
                case 'Enter':
                case ' ':
                case 'Right':
                case 'ArrowRight':
                  setPath(nestedPath);
                  break;
                default:
                  break;
              }
            };

            const attributes = {
              ref: menuItemRef,
              tabIndex: 0,
              key: `menu-item-${key}`,
              onKeyDown: _onKeyDown,
              style: { pointerEvents: 'auto' },
              onMouseEnter: () => {
                if (menuItemRef?.current != null) menuItemRef.current.focus();
                setPath(nestedPath);
              },
              onKeyUp: handleKeyUp,
            };

            const child = menuItems?.[key];

            if (typeof child === 'function') {
              return child({
                element,
                text: key,
                onClose,
                openFileDialog,
                attributes,
              });
            }

            const RenderSubMenuItem = renderSubMenuItem;

            const content = (
              <RecursiveNestedMenu
                onClose={onClose}
                text={key}
                path={nestedPath}
                currentPath={currentPath}
                setPath={setPath}
                key={`nested-menu-item-${key}`}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                onKeyDown={_onKeyDown}
                onMouseEnter={() => {
                  if (menuItemRef?.current != null) menuItemRef.current.focus();
                  setPath(nestedPath);
                }}
                onMouseLeave={() => {
                  setPath(path);
                }}
                openFileDialog={openFileDialog}
                disablePortal={false}
                hideBackdrop
                menuItems={menuItems?.[key]}
                element={element}
                anchorEl={menuItemRef.current as any}
                open={isOpen}
              />
            );

            return typeof menuItems?.[key] !== 'function' ? (
              <RenderSubMenuItem
                key={`menu-item-${key}`}
                {...{
                  element,
                  text: key,
                  onClose,
                  openFileDialog,
                  children: content,
                  setPath,
                  path: nestedPath,
                  attributes,
                }}
              />
            ) : (
              content
            );
          })}
    </NestedMenuContainer>
  ) : null;
};

export const NestedMenu = (
  props: Pick<
    NestedMenuProps,
    | 'menuItems'
    | 'element'
    | 'renderSubMenuItem'
    | 'openFileDialog'
    | 'open'
    | 'anchorEl'
    | 'onClose'
  >,
) => {
  const [currentPath, setPath] = useState<string[]>([]);

  return (
    <RecursiveNestedMenu
      {...props}
      disablePortal
      path={[]}
      tabIndex={-1}
      setPath={setPath}
      currentPath={currentPath}
    />
  );
};
